import { i18next } from "@holibob-packages/locales";

import { formatDuration } from "../formatDuration";
import {
    CancellationPenaltyPercentage,
    CancellationPenaltyAbsolute,
    CancellationPenaltyCalculationTypeSchema,
    ProductPriceTypeSchema,
    ProductPriceType,
} from "../types";
import { CancellationPenaltyFormattedTextParams } from "./generateCancellationPenaltyFormattedText";

type Params = CancellationPenaltyFormattedTextParams & {
    priceType?: ProductPriceType;
};

export function generateCancellationPenaltyFormattedTextTravelDate(params: Params) {
    const { cancellationPenaltyList, languageCode, priceType } = params;

    const i18n = i18next.cloneInstance({ lng: languageCode });
    if (priceType === ProductPriceTypeSchema.Enum.FREE) {
        return `${i18n.t("refundPolicy:free.noPenalties")}.`;
    }

    const amountStatement = generateAmountStatementText(params);
    const count = cancellationPenaltyList.length;
    if (count === 1) {
        return `${amountStatement}.`;
    }

    const timeStatement = generateTimeStatementText(params);

    return `${amountStatement} ${timeStatement}.`;
}

function generateAmountStatementText(params: Params) {
    const { cancellationPenaltyList, formattedPenaltyIndex } = params;
    const cancellationPenalty = cancellationPenaltyList[formattedPenaltyIndex];

    if (!cancellationPenalty) {
        return "";
    }

    const { languageCode, totalPrice, calculationType = "PERCENTAGE" } = params;

    if (cancellationPenalty.type === "PERCENTAGE") {
        return generatePercentageRefundPolicyText({
            languageCode,
            cancellationPenalty,
            totalPrice,
            calculationType,
        });
    }

    return generateAbsoluteAmountRefundPolicyText(languageCode, cancellationPenalty);
}
type GeneratePercentageRefundPolicyTextParams = Pick<Params, "languageCode" | "totalPrice" | "calculationType"> & {
    cancellationPenalty: CancellationPenaltyPercentage;
};
function generatePercentageRefundPolicyText(params: GeneratePercentageRefundPolicyTextParams) {
    const { languageCode, cancellationPenalty, totalPrice } = params;
    const { calculationType = CancellationPenaltyCalculationTypeSchema.Enum.ABSOLUTE } = params;

    const i18n = i18next.cloneInstance({ lng: languageCode });

    const { refundPercentage } = cancellationPenalty;
    if (refundPercentage === 0) {
        return i18n.t("refundPolicy:amountStatement.noRefund");
    }

    if (totalPrice && calculationType === "ABSOLUTE") {
        const numberFormat = new Intl.NumberFormat(i18n.languages[0], {
            style: "currency",
            currency: totalPrice.currency,
            currencyDisplay: "code",
        });

        if (refundPercentage === 100) {
            return i18n.t("refundPolicy:amountStatement.fullRefundAmount", {
                refundAmount: numberFormat.format(totalPrice.gross),
            });
        }

        return i18n.t("refundPolicy:amountStatement.refundAmount", {
            refundAmount: numberFormat.format((totalPrice.gross * refundPercentage) / 100),
        });
    }

    if (refundPercentage === 100) {
        return i18n.t("refundPolicy:amountStatement.fullRefund");
    }

    return i18n.t("refundPolicy:amountStatement.refundPercentage", { refundPercentage });
}

function generateAbsoluteAmountRefundPolicyText(lng: string, cancellationPenalty: CancellationPenaltyAbsolute) {
    const { amount, amountType, amountCurrency } = cancellationPenalty;
    const i18n = i18next.cloneInstance({ lng });

    const numberFormat = new Intl.NumberFormat(i18n.languages[0], {
        style: "currency",
        currency: amountCurrency,
        currencyDisplay: "code",
    });

    if (amount === 0) {
        if (amountType === "CHARGE") {
            return i18n.t("refundPolicy:amountStatement.fullRefund");
        } else {
            return i18n.t("refundPolicy:amountStatement.noRefund");
        }
    }

    const amountTypeStatement =
        amountType === "CHARGE"
            ? i18n.t("refundPolicy:amountStatement.absolute.charge")
            : i18n.t("refundPolicy:amountStatement.absolute.refund");

    const amountStatement = `${numberFormat.format(amount)}`;
    return i18n.t("refundPolicy:amountStatement.absolute.statement", { amountStatement, amountTypeStatement });
}

function generateTimeStatementText(params: Params) {
    const { languageCode, formattedPenaltyIndex, cancellationPenaltyList } = params;
    const cancellationPenalty = cancellationPenaltyList[formattedPenaltyIndex] ?? { duration: null };
    const nextCancellationPenalty = cancellationPenaltyList[formattedPenaltyIndex + 1] ?? { duration: null };
    const isLastPenalty = formattedPenaltyIndex === cancellationPenaltyList.length - 1;
    const i18n = i18next.cloneInstance({ lng: languageCode });

    if (isLastPenalty) {
        return i18n.t("refundPolicy:timeStatement.travelDate.within", {
            durationStatement: formatDuration(cancellationPenalty.duration, languageCode),
        });
    }

    return i18n.t("refundPolicy:timeStatement.travelDate.atLeastBefore", {
        durationStatement: formatDuration(nextCancellationPenalty.duration, languageCode),
    });
}
